/**
 * The server's rules, restated so the person typing learns before they submit.
 *
 * **This decides nothing.** Every rule here is mirrored from the service, and
 * the service is authoritative (FR-016). Nothing in this module gates the
 * submit button: a disabled button carrying a stale rule cannot be fixed from
 * the backend, while an enabled one that sends and surfaces the service's answer
 * always degrades correctly.
 *
 * Duplicated logic drifts, and this is duplicated logic. It is accepted for one
 * reason — the reserved-prefix rule is impossible to guess, and discovering it
 * only after a round-trip is the difference between a hint and a puzzle.
 *
 * Each constant names its counterpart in
 * `product-x-next-service/src/app/features/secrets/validation.py`, so a
 * reviewer changing one knows the other exists.
 */

/** Mirrors `validation.KEY_PATTERN`. */
export const KEY_PATTERN = /^[A-Z_][A-Z0-9_]*$/;

/** Mirrors `validation.RESERVED_PREFIXES`. */
export const RESERVED_PREFIXES = ['PRODUCT_X_NEXT_', 'ANTHROPIC_'] as const;

/** Mirrors `validation.MAX_VALUE_BYTES`. */
export const MAX_VALUE_BYTES = 64 * 1024;

/** Mirrors `validation.MAX_DESCRIPTION_CHARS`. */
export const MAX_DESCRIPTION_CHARS = 500;

/**
 * A problem with the key, or `null`.
 *
 * The reserved case is checked after the pattern and reported differently: a
 * reserved key is correctly formed, and calling it malformed would be a wrong
 * answer rather than an incomplete one.
 */
export function validateKey(key: string): string | null {
  if (key.length === 0) return 'Enter a name.';
  if (!KEY_PATTERN.test(key)) {
    return 'Use capital letters, digits and underscores, not starting with a digit.';
  }
  const reserved = RESERVED_PREFIXES.find((prefix) => key.startsWith(prefix));
  if (reserved) {
    return `Names starting with ${reserved} are reserved by the platform. Choose a different one.`;
  }
  return null;
}

/**
 * A problem with the value, or `null`.
 *
 * Measured in UTF-8 bytes, not characters, because that is what the service
 * measures. A mirror counting `value.length` would accept a string of emoji
 * four times over the real limit and then watch the server refuse it — the one
 * way this mirror could actively mislead rather than merely lag.
 */
export function validateValue(value: string): string | null {
  const bytes = new TextEncoder().encode(value).length;
  if (bytes > MAX_VALUE_BYTES) {
    return `That value is ${Math.round(bytes / 1024)} KB. The limit is 64 KB.`;
  }
  return null;
}

/**
 * A problem with the description, or `null`.
 *
 * Counted in **code points**, not `.length`. JavaScript's `.length` counts
 * UTF-16 code units, so every astral character — emoji, and a good deal of CJK
 * extension — counts twice, and a mirror using it would refuse a 300-emoji
 * description that the service accepts. That is the same class of bug
 * `validateValue`'s comment warns about for bytes: the one way this mirror can
 * actively mislead rather than merely lag. The service counts code points, so
 * this counts code points.
 *
 * Line breaks are refused because the inventory the service renders into every
 * execution's prompt is one entry per line, and a description carrying a
 * newline turns that list into a shape the reader has to infer.
 */
export function validateDescription(description: string): string | null {
  const text = description.trim();
  if (text === '') return null;
  if (/[\n\r\u2028\u2029]/.test(text)) {
    return 'Keep the description on a single line.';
  }
  const length = [...text].length;
  if (length > MAX_DESCRIPTION_CHARS) {
    return `That description is ${String(length)} characters. The limit is ${String(MAX_DESCRIPTION_CHARS)}.`;
  }
  return null;
}

/** How many code points a description uses, for the live counter. */
export function descriptionLength(description: string): number {
  return [...description.trim()].length;
}
